import { useEffect, useState } from "react";
import { MskuPrepDetail } from "@boxem/core-api-schema";

import styles from "./fba-products-list.module.css";

import { useFocus } from "@/hooks/useFocus";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import TextInput from "@/components/forms/TextInput/TextInput";
import IconSearch from "@/components/icons/IconSearch";
import Button from "@/components/forms/Button/Button";
import ProductListItemSkeleton from "@/components/ProductListItemSkeleton/ProductListItemSkeleton";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Link from "@/components/Link/Link";
import Pagination from "@/components/Pagination/Pagination";
import FbaProductSearchListItem from "../FbaProductSearchListItem/FbaProductSearchListItem";
import FbaBatchItemDrawer from "../FbaBatchItemDrawer/FbaBatchItemDrawer";

let searchTimeoutId;

type FbaProductsListProps = {};

const FbaProductsList = ({}: FbaProductsListProps) => {
  const {isFetchingInventory, inventory, getPrepDetailsForSKUs, getSalesRanksForASINs } = useInventoryContext();
  const {handleErrorResponseGenerically} = useErrorModalContext();

  const [searchInputRef, setSearchInputFocus] = useFocus<HTMLInputElement>();
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [isLoadingSearchResults, setIsLoadingSearchResults] = useState<boolean>(false);
  const [inventoryResults, setInventoryResults] = useState<Product[]>([]);
  const [inventoryView, setInventoryView] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  interface AddingProduct {
    product: Product;
    sellerDataIndex: number;
    prepDetails: MskuPrepDetail;
  }
  const [addingProduct, setAddingProduct] = useState<AddingProduct | null>(null);

  const finalizeSearchResults = async (inventorySearchResults: Product[]) => {
    const start = performance.now();

    setInventoryResults(inventorySearchResults);

    if (inventorySearchResults.length > 0) {
      setIsLoadingSearchResults(true); // we have to set this here as well as in one calling context, because they are sometimes used indepdendently

      // ensure the prep details for all the results are cached (used in the add to shipment form):
      const allSKUs: string[] = [];
      inventorySearchResults.forEach(isr => {
        isr.sellerData.forEach(sd => allSKUs.push(sd.sku));
      });
      getPrepDetailsForSKUs(allSKUs, true); // discarding result because we're just warming the cache here

      // get the slice for the viewable portion based on page size:
      const pageSize = 8; // we hardcode this because 10 is actually a constraint relating to rate-limits (of the sales-rank request)
      const startIndex = (currentPage-1)*pageSize;
      const endIndex = (currentPage-1)*pageSize + pageSize;
      const inventoryView: Product[] = inventorySearchResults.slice(startIndex, Math.min(endIndex, inventorySearchResults.length));

      // get the sales ranks for the search results:
      try {
        const salesRanks = await getSalesRanksForASINs(inventoryView.map(i => i.asin));
        inventoryView.forEach(product => {
          const salesRank = salesRanks.find(salesRank => salesRank.asin === product.asin);
          if (salesRank) {
            product.salesRank = salesRank.salesRank;
            product.category = salesRank.category;
            product.categoryLink = salesRank.link;
            product.hazmat = salesRank.hazmat;
          }
       });      
      } catch (e: any) {
        if (handleErrorResponseGenerically(e)) {
          return;
        }
      }

      setInventoryView(inventoryView);
    } else {
      setInventoryView([]);
    }

    // important to set these after the sales rank api request, otherwise we can get weird situations
    // where the pagination appears ~0.5s before the search results does.
    setIsLoadingSearchResults(false);
    console.info(`finaling search results took: ${performance.now() - start}. ${inventorySearchResults.length} inventory results`);
  }

  const debouncedSearch = async (searchText: string) => {
    // sometimes during transition page in a state where it shows both 
    // no product matched message
    // as well as search results
    // set isLoadingSearchResults first to true fixes it
    setIsLoadingSearchResults(true);
    clearTimeout(searchTimeoutId);

    let searchTerm = searchText.trim();
    setCurrentPage(1);

    if (searchTerm.length === 0) {
      setIsLoadingSearchResults(false);
      // when using backspace to remove search terms it shows no results but still
      // keeps the previous pagination. we need to clean up the results
      await finalizeSearchResults([]);
      return;
    }
    searchTerm = searchTerm.toLowerCase();    
    const start = performance.now();

    searchTimeoutId = setTimeout(async () => {
      setInventoryResults([]);

      const searchTermValues = searchTerm.split(" "); //Split on spaces - potential future upgrade split multiple characters.        
      const inventoryResults: Product[] = [];
      for (let i = 0; i < inventory.length; i++) {
        const inventoryItem: Product = inventory[i];
        const foundItem = searchTermValues.filter(term => inventoryItem.searchString.includes(term)); //Filter out terms not found in search string.
        if (foundItem.length === searchTermValues.length) inventoryResults.push(inventoryItem); //Push inv item if all terms were present in search string.
      }
      
      await finalizeSearchResults(inventoryResults);
      console.info(`search took: ${performance.now() - start}, including a 350ms debounce wait.`);
    }, 350);
  };

  useEffect(() => {
    finalizeSearchResults(inventoryResults);
  }, [currentPage, inventory]);

  // @NOTE(nick) I wanted to avoid this, and instead just wait on the inventory fetch
  // inside of the search function itself, which would make refreshing on this page
  // snappier, but that doesn't work, because:
  //  - if you await a new promise inside the search function which runs a setInterval repeatedly checking for the fetch to be done, the closure will use the captured value of 'isFetchingInventory', not the up-to-date one, resulting in it always being == true
  //  - if you instead expose a Promise from the inventory context itself which is the fetch request, it will resolve before we necessarily render with the inventory, due to the async nature of setState calls
  // there might be another way.
  if (isFetchingInventory) {
    return (
      <div className={styles.container}>
        <Flex align="center" gap={4}>
          <LoadingSpinner /> Loading your inventory...
        </Flex>
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        <Flex gap={4}>
          <TextInput
            ref={searchInputRef}
            placeholder="Search by product name, SKU, FNSKU, ASIN, UPC, ISBN, EAN, or GTIN"
            prefix={<Flex align="center" style={{marginRight:"1ch"}}><IconSearch /></Flex>}
            value={searchInputText}
            onChange={v => {
              setSearchInputText(v);
              debouncedSearch(v);
            }}
            maxLength={300}
            inputAutofocus
          />
        </Flex>
        <div className={styles.productListBox}>
          {/* No active search */}
          {searchInputText === "" && (
            <Flex
              direction="column"
              className={styles.emptyBox}
            >
              <img src="/images/search.svg" height="100" />
              <p>{`Search by typing a search query above.`}</p>
            </Flex>
          )}
          {/* We aren't loading a search, we have no search results, and the search string isn't empty. 'No results' case. */}
          {!isLoadingSearchResults && inventoryResults.length === 0 && searchInputText !== "" &&
            <Flex
              direction="column"
              className={styles.emptyBox}
            >
              <span className="strong">
                {`No product found matching "${searchInputText}"`}
              </span>
              <img src="/images/search.svg" height="100" />
              <p>Looking to list new SKUs? Click <Link href={'/fba/listings/create'}>here</Link> for the List FBA flow. </p>
              <Button onClick={() => { setSearchInputText(""); setSearchInputFocus(); }} type="secondary">
                Clear search
              </Button>
            </Flex>
          }
          {/* loading a search... */}
          {isLoadingSearchResults && (
            <Flex
              direction="column"
              className={styles.resultsArea}
            >
              <ProductListItemSkeleton />
              <ProductListItemSkeleton />
              <ProductListItemSkeleton />
            </Flex>
          )}
          {/* inventory results, if any: */}
          {(!isLoadingSearchResults && searchInputText !== "" && inventoryView.length > 0) && (
            <Flex
              direction="column"
              className={styles.resultsArea}
            >
              {inventoryView.map((item, index) => {
                return (
                  <FbaProductSearchListItem
                    key={index}
                    product={item}
                    onAddToShipment={async (product: Product, sellerDataIndex: number) => {
                      const [errors, prepDetails] = await getPrepDetailsForSKUs([product.sellerData[sellerDataIndex].sku]);
                      if (errors) {
                        handleErrorResponseGenerically(errors);
                        return;
                      }
                      setAddingProduct({
                        product,
                        sellerDataIndex,
                        prepDetails: prepDetails[0],
                      });
                    }}
                  />
                );
              })}
              {<div className={styles.fbaListingFlowLinkSection}>
                <p>Looking to list new SKUs? Click <Link href={'/fba/listings/create'}>here</Link> for the List FBA
                  flow.
                </p>
              </div>}
            </Flex>
          )}
        </div>

        {(inventoryResults.length !== 0 && (inventoryResults.length > 10)) && (
          <Pagination
            sticky
            showPageSizeOptions={false}

            total={inventoryResults.length}

            pageSize={10}
            onChangePageSize={() => {}}

            page={currentPage}
            onChangePage={(e) => setCurrentPage(e) }
          />
        )}
      </div>

      {addingProduct && (
        <FbaBatchItemDrawer
          mode="ADD_TO_BATCH"
          product={addingProduct.product}
          sellerDataIndex={addingProduct.sellerDataIndex}
          prepDetails={addingProduct.prepDetails}
          onClose={() => {
            setSearchInputFocus();
            setAddingProduct(null);
          }}
        />
      )}
    </>
  );
};

export default FbaProductsList;
